import { useState, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Divider, Empty, Typography, Select, Tabs, Input, InputNumber, Switch, ColorPicker, Slider, Button, Space, message } from 'antd';
import { PlusOutlined, DeleteOutlined, FolderOpenOutlined } from '@ant-design/icons';
import useDashboardStore from '../../stores/dashboardStore';
import { widgetRegistry } from '../../registry/widgetRegistry';
import type { WidgetSchemaField } from '@dtwin/shared';
import AssetLibraryModal from './AssetLibraryModal';
import DataBindingPanel from './DataBindingPanel';

const { Text, Title } = Typography;

// 读取组件属性（base > special > config > style > default）
function readValue(comp: any, field: string, def: unknown): any {
  if (field === 'x') return comp.position?.x ?? def;
  if (field === 'y') return comp.position?.y ?? def;
  if (field === 'width') return comp.size?.width ?? def;
  if (field === 'height') return comp.size?.height ?? def;
  if (comp?.base && comp.base[field] !== undefined) return comp.base[field];
  if (comp?.special && comp.special[field] !== undefined) return comp.special[field];
  if (comp?.config && comp.config[field] !== undefined) return comp.config[field];
  if (comp?.style && comp.style[field] !== undefined) return comp.style[field];
  return def;
}

function UploadControl({ comp, field, onUpdate }: { comp: any; field: WidgetSchemaField; onUpdate: (value: unknown) => void }) {
  const [uploading, setUploading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const value = String(readValue(comp, field.field, '') || '');

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const { uploadDashboardAsset } = await import('../../api/assets');
      const res = await uploadDashboardAsset(file);
      onUpdate(res.url);
      message.success('上传成功');
      setLibOpen(false);
    } catch (e: any) {
      const detail = e?.response?.data?.msg || e?.response?.data?.message || e?.message || '未知错误';
      message.error(`上传失败: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSelectAsset = (url: string) => {
    onUpdate(url);
    message.success('已引用素材');
    setLibOpen(false);
  };

  return (
    <div>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          size="small"
          value={value}
          placeholder={field.hint || '图片 URL 或点击上传'}
          onChange={(e) => onUpdate(e.target.value)}
        />
        <Button size="small" icon={<FolderOpenOutlined />} title="打开素材库" onClick={() => setLibOpen(true)} />
        {/* 2026-09-07｜2.4 修复：antd Button 包在 label 内点击不触发隐藏 input（Button 拦截 label 默认行为），改 Button onClick 显式触发 */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
            e.target.value = '';
          }}
        />
        <Button size="small" loading={uploading} onClick={() => fileInputRef.current?.click()}>{uploading ? '上传中...' : '上传'}</Button>
      </Space.Compact>
      {value && (
        <div style={{ marginTop: 6, position: 'relative', width: '100%', height: 80, overflow: 'hidden', borderRadius: 4, border: '1px solid #333' }}>
          <img src={value} alt="预览" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      )}
      <AssetLibraryModal open={libOpen} onClose={() => setLibOpen(false)} onSelect={handleSelectAsset} />
    </div>
  );
}

function ShadowControl({ comp }: { comp: any }) {
  const { updateComponent } = useDashboardStore();

  // 组装 box-shadow 写回 boxShadow 字段 + 拆分字段（存 base 层，全局可同步）
  const writeShadow = (patch: Record<string, any>) => {
    const x = (patch.shadowX !== undefined ? patch.shadowX : (Number(readValue(comp, 'shadowX', 0)) || 0)) as number;
    const y = (patch.shadowY !== undefined ? patch.shadowY : (Number(readValue(comp, 'shadowY', 0)) || 0)) as number;
    const blur = (patch.shadowBlur !== undefined ? patch.shadowBlur : (Number(readValue(comp, 'shadowBlur', 8)) || 0)) as number;
    const color = (patch.shadowColor !== undefined ? patch.shadowColor : String(readValue(comp, 'shadowColor', '#0096ff') || '#0096ff')) as string;
    const enabled = (patch.shadowEnabled !== undefined ? patch.shadowEnabled : readValue(comp, 'shadowEnabled', true)) as boolean;
    const base = {
      ...(comp.base || {}),
      shadowX: patch.shadowX !== undefined ? patch.shadowX : x,
      shadowY: patch.shadowY !== undefined ? patch.shadowY : y,
      shadowBlur: patch.shadowBlur !== undefined ? patch.shadowBlur : blur,
      shadowColor: patch.shadowColor !== undefined ? patch.shadowColor : color,
      shadowEnabled: enabled,
      boxShadow: enabled ? `${x}px ${y}px ${blur}px ${toRgba(color)}` : 'none',
    };
    updateComponent(comp.id, { base });
  };
  const toRgba = (c: string) => {
    if (c.startsWith('#')) {
      const h = c.replace('#', '');
      const r = parseInt(h.slice(0, 2), 16); const g = parseInt(h.slice(2, 4), 16); const b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.2)`;
    }
    if (c.startsWith('rgba')) return c;
    if (c.startsWith('rgb')) return c.replace('rgb(', 'rgba(').replace(')', ', 0.2)');
    return c;
  };

  const legacy = String(readValue(comp, 'boxShadow', '') || '');
  const enabled = !!readValue(comp, 'shadowEnabled', legacy && legacy !== 'none');
  const x = Number(readValue(comp, 'shadowX', 0)) || 0;
  const y = Number(readValue(comp, 'shadowY', 0)) || 0;
  const blur = Number(readValue(comp, 'shadowBlur', 8)) || 0;
  const color = String(readValue(comp, 'shadowColor', '#0096ff') || '#0096ff');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#888' }}>启用</span>
        <Switch size="small" checked={enabled} onChange={(v) => writeShadow({ shadowEnabled: v })} />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#666' }}>{x}px · {y}px · {blur}px</span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>水平偏移 X</Text>
          <InputNumber size="small" style={{ width: '100%' }} min={-50} max={50} value={x}
            onChange={(v) => writeShadow({ shadowX: v ?? 0 })} />
        </div>
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>垂直偏移 Y</Text>
          <InputNumber size="small" style={{ width: '100%' }} min={-50} max={50} value={y}
            onChange={(v) => writeShadow({ shadowY: v ?? 0 })} />
        </div>
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>模糊</Text>
          <InputNumber size="small" style={{ width: '100%' }} min={0} max={60} value={blur}
            onChange={(v) => writeShadow({ shadowBlur: v ?? 0 })} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, color: '#888' }}>颜色</span>
        <ColorPicker size="small" value={color.startsWith('#') ? color : '#0096ff'}
          onChange={(c) => writeShadow({ shadowColor: c.toHexString() })} />
        <span style={{ fontSize: 11, color: '#555' }}>默认 20% 透明度</span>
      </div>
    </div>
  );
}

function SchemaControl({ comp, field, onUpdate }: { comp: any; field: WidgetSchemaField; onUpdate: (value: unknown) => void }) {
  const value = readValue(comp, field.field, field.default);
  const common = { size: 'small' as const, style: { width: '100%' as const } };

  switch (field.control) {
    case 'hidden': return null;
    case 'switch':
      return <Switch size="small" checked={!!value} onChange={(v) => onUpdate(v)} />;
    case 'color':
      return (
        <ColorPicker
          size="small"
          value={typeof value === 'string' && value.startsWith('#') ? value : undefined}
          onChange={(c) => onUpdate(c.toHexString())}
          allowClear
        />
      );
    case 'select':
      return (
        <Select
          size="small" style={{ width: '100%' }}
          value={value} options={field.options} onChange={(v) => onUpdate(v)}
        />
      );
    case 'font': {
      const FONT_OPTIONS = [
        { value: '', label: '默认（继承）' },
        { value: '"Microsoft YaHei", "微软雅黑", sans-serif', label: '微软雅黑' },
        { value: '"SimSun", "宋体", serif', label: '宋体' },
        { value: '"SimHei", "黑体", sans-serif', label: '黑体' },
        { value: '"KaiTi", "楷体", serif', label: '楷体' },
        { value: '"FangSong", "仿宋", serif', label: '仿宋' },
        { value: '"Noto Sans SC", "思源黑体", sans-serif', label: '思源黑体' },
        { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
        { value: 'Georgia, serif', label: 'Georgia' },
        { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
        { value: '"Courier New", monospace', label: 'Courier New' },
      ];
      return (
        <Select size="small" style={{ width: '100%' }} value={value || ''} options={FONT_OPTIONS} onChange={(v) => onUpdate(v)} />
      );
    }
    case 'number':
      return (
        <InputNumber
          size="small" style={{ width: '100%' }}
          min={field.min} max={field.max} step={field.step}
          value={Number(value) || 0} onChange={(v) => onUpdate(v ?? 0)}
        />
      );
    case 'slider':
      return (
        <Slider
          min={field.min ?? 0} max={field.max ?? 1} step={field.step ?? 0.05}
          value={Number(value) || 0} onChange={(v) => onUpdate(v)}
        />
      );
    case 'json':
      return (
        <Input.TextArea
          rows={3} placeholder={field.hint}
          value={typeof value === 'string' ? value : value ? JSON.stringify(value, null, 2) : ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw.trim()) { onUpdate(''); return; }
            try { onUpdate(JSON.parse(raw)); } catch { /* 保留文本，待合法后提交 */ }
          }}
        />
      );
    case 'shadow':
      return (
        <ShadowControl comp={comp} />
      );
    case 'upload':
      return (
        <UploadControl comp={comp} field={field} onUpdate={onUpdate} />
      );
    case 'textarea':
      return (
        <Input.TextArea
          rows={3}
          value={String(value ?? '')}
          onChange={(e) => onUpdate(e.target.value)}
        />
      );
    default: // input
      return (
        <Input
          {...common}
          value={String(value ?? '')}
          placeholder={field.hint}
          onChange={(e) => onUpdate(e.target.value)}
        />
      );
  }
}

function FieldRow({ field, comp, onUpdate }: { field: WidgetSchemaField; comp: any; onUpdate: (key: string, value: unknown) => void }) {
  return (
    <div style={{ marginBottom: 10 }} data-testid={`cf-field-${field.field}`}>
      <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>{field.label}</Text>
      <SchemaControl comp={comp} field={field} onUpdate={(v) => onUpdate(field.field, v)} />
    </div>
  );
}

function BaseForm({ comp }: { comp: any }) {
  const { updateComponent } = useDashboardStore();
  const schema = widgetRegistry.getSchema(comp.type);

  const onUpdate = (key: string, value: unknown) => {
    if (key === 'x' || key === 'y') {
      updateComponent(comp.id, { position: { ...(comp.position || { x: 0, y: 0 }), [key]: Number(value) } });
    } else if (key === 'width' || key === 'height') {
      updateComponent(comp.id, { size: { ...(comp.size || { width: 400, height: 300 }), [key]: Number(value) } });
    } else if (comp.base && comp.base[key] !== undefined) {
      // 2026-09-19｜B2-M-04：展示优先级 base > special > config，回写需与之一致，
      // 否则 base.title 已存在时改标题写入 config 被遮蔽（看不到变化）
      updateComponent(comp.id, { base: { ...comp.base, [key]: value } });
    } else if (comp.special && comp.special[key] !== undefined) {
      updateComponent(comp.id, { special: { ...comp.special, [key]: value } } as any);
    } else {
      const cfg = comp.config || {};
      updateComponent(comp.id, { config: { ...cfg, [key]: value } });
    }
  };

  // 2026-09-01｜字体选项：商业免费主流字体（系统自带/开源，无需授权）
  const FONT_OPTIONS = [
    { value: '', label: '默认（继承）' },
    { value: '"Microsoft YaHei", "微软雅黑", sans-serif', label: '微软雅黑' },
    { value: '"SimSun", "宋体", serif', label: '宋体' },
    { value: '"SimHei", "黑体", sans-serif', label: '黑体' },
    { value: '"KaiTi", "楷体", serif', label: '楷体' },
    { value: '"FangSong", "仿宋", serif', label: '仿宋' },
    { value: '"Noto Sans SC", "思源黑体", sans-serif', label: '思源黑体' },
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
    { value: '"Courier New", monospace', label: 'Courier New' },
  ];

  return (
    <>
      {schema.filter((f) => f.group === 'base').map((f) => (
        <FieldRow key={f.field} field={f} comp={comp} onUpdate={onUpdate} />
      ))}
      {/* 2026-09-07｜2.2：字体位置调整——组件字体移入控件特有属性（各控件字号附近），通用属性仅保留标题字体 */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>标题字体</Text>
        <Select
          size="small"
          style={{ width: '100%' }}
          value={comp.config?.titleFontFamily || ''}
          onChange={(v) => onUpdate('titleFontFamily', v)}
          options={FONT_OPTIONS}
        />
      </div>
    </>
  );
}

function SpecialForm({ comp }: { comp: any }) {
  const { updateComponent } = useDashboardStore();
  const schema = widgetRegistry.getSchema(comp.type);
  // 2026-09-14 修复（FE-12）：regular 层（图表常规属性：图例显示/位置/方向、坐标轴字号）此前全仓无 UI 消费 → 在此渲染
  const regularFields = schema.filter((f) => f.group === 'regular');
  const specialFields = schema.filter((f) => f.group === 'special');

  const onUpdate = (key: string, value: unknown) => {
    const cfg = comp.config || {};
    updateComponent(comp.id, { config: { ...cfg, [key]: value } });
  };

  if (regularFields.length === 0 && specialFields.length === 0) {
    return <Text style={{ fontSize: 12, color: '#666' }}>该控件无特有配置项</Text>;
  }

  return (
    <>
      {regularFields.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>常规属性</div>
          {regularFields.map((f) => (
            <FieldRow key={f.field} field={f} comp={comp} onUpdate={onUpdate} />
          ))}
          {specialFields.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 12, color: '#888', marginBottom: 6 }}>
              控件特有
            </div>
          )}
        </>
      )}
      {specialFields.map((f) => (
        <FieldRow key={f.field} field={f} comp={comp} onUpdate={onUpdate} />
      ))}
    </>
  );
}

function EventForm({ comp }: { comp: any }) {
  const { updateComponent, components } = useDashboardStore();
  const events = (comp.events || comp.eventList || []) as any[];
  const [deviceOptions, setDeviceOptions] = useState<{ value: string; label: string }[]>([]);

  // 阶段3｜3.3：3D 联动动作的设备选择复用设备注册表（名称+IMEI 搜索，与设备数据源选择器同一来源）
  useEffect(() => {
    import('../../api/iot').then(({ fetchIotDevices }) =>
      fetchIotDevices().then((list) => {
        setDeviceOptions(
          list.map((d) => ({
            value: d.deviceId,
            label: `${d.name}${d.imei && d.imei !== d.name ? ` (${d.imei})` : ''}`,
          })),
        );
      }).catch(() => {}),
    ).catch(() => {});
  }, []);

  const updateEvents = (list: any[]) => {
    updateComponent(comp.id, { events: list });
  };

  const addEvent = () => {
    updateEvents([...events, { id: `evt_${Date.now()}`, trigger: 'click', action: 'refresh', targetId: '' }]);
  };
  const removeEvent = (idx: number) => updateEvents(events.filter((_, i) => i !== idx));
  const setEvent = (idx: number, patch: Record<string, unknown>) => {
    updateEvents(events.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  // 组件友好名称：title/name > 类型 + 坐标
  const compName = (c: any) => {
    const title = c?.base?.title ?? c?.config?.title ?? c?.special?.title;
    if (title) return String(title);
    const nm = c?.base?.name ?? c?.config?.name;
    if (nm) return String(nm);
    return `${c.type} (${Math.round(c.position?.x ?? 0)},${Math.round(c.position?.y ?? 0)})`;
  };

  // 目标组件下拉选项：当前编辑器内全部组件（排除自身），可搜索
  const targetOptions = useMemo(
    () =>
      components
        .filter((c) => c.id !== comp.id)
        .map((c) => ({
          value: c.id,
          label: compName(c),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [components],
  );

  return (
    <div>
      {events.length === 0 && (
        <Text style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 8 }}>暂无联动事件</Text>
      )}
      {events.map((ev, idx) => (
        <div key={ev.id} style={{ border: '1px solid #333', borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <Space style={{ width: '100%', display: 'flex' }} size={6}>
            <Select
              size="small" style={{ width: 80 }}
              value={ev.trigger} options={[{ value: 'click', label: '点击' }, { value: 'hover', label: '悬停' }]}
              onChange={(v) => setEvent(idx, { trigger: v })}
            />
            <Select
              size="small" style={{ flex: 1 }}
              value={ev.action}
              options={[
                { value: 'refresh', label: '刷新目标' },
                { value: 'filter', label: '筛选目标' },
                { value: 'passParam', label: '传递参数' },
                { value: 'openUrl', label: '打开链接' },
                { value: 'switchTab', label: '切换页签' },
                { value: 'locate3dDevice', label: '定位3D设备' },
                { value: 'highlight3dDevice', label: '高亮3D设备' },
                { value: 'linkGlobalDevice', label: '设备全局联动' },
              ]}
              onChange={(v) => setEvent(idx, { action: v })}
            />
            <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => removeEvent(idx)} />
          </Space>
          {(ev.action === 'refresh' || ev.action === 'filter' || ev.action === 'passParam' || ev.action === 'switchTab') && (
            <Select
              size="small"
              style={{ marginTop: 6, width: '100%' }}
              placeholder="选择目标组件"
              showSearch
              optionFilterProp="label"
              value={ev.targetId || undefined}
              options={targetOptions}
              onChange={(v) => setEvent(idx, { targetId: v })}
            />
          )}
          {(ev.action === 'filter' || ev.action === 'passParam') && (
            <Input
              size="small" style={{ marginTop: 6 }}
              placeholder="筛选字段"
              value={ev.filterField || ''}
              onChange={(e) => setEvent(idx, { filterField: e.target.value })}
            />
          )}
          {ev.action === 'openUrl' && (
            <Input
              size="small" style={{ marginTop: 6 }}
              placeholder="https://..."
              value={ev.url || ''}
              onChange={(e) => setEvent(idx, { url: e.target.value })}
            />
          )}
          {(ev.action === 'locate3dDevice' || ev.action === 'highlight3dDevice' || ev.action === 'linkGlobalDevice') && (
            <Select
              size="small"
              style={{ marginTop: 6, width: '100%' }}
              placeholder="选择设备（名称 / IMEI）"
              showSearch
              filterOption={(input: string, option: any) =>
                [option?.label, option?.value].filter(Boolean).some((s) => String(s).toLowerCase().includes(input.toLowerCase()))
              }
              value={ev.deviceId || undefined}
              options={deviceOptions}
              onChange={(v) => setEvent(idx, { deviceId: v })}
              notFoundContent="暂无设备：请到「插件 → RTU 设备管理」添加/绑定，或手动输入"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Input
                    size="small"
                    style={{ margin: 4, width: 'calc(100% - 8px)' }}
                    placeholder="或手动输入设备ID / IMEI"
                    allowClear
                    onPressEnter={(e) =>
                      setEvent(idx, { deviceId: (e.target as HTMLInputElement).value.trim() })
                    }
                    onBlur={(e) => {
                      const v = (e.target as HTMLInputElement).value.trim();
                      if (v) setEvent(idx, { deviceId: v });
                    }}
                  />
                </>
              )}
            />
          )}
        </div>
      ))}
      <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ width: '100%' }} onClick={addEvent}>
        添加联动事件
      </Button>
    </div>
  );
}

export default function ConfigPanel() {
  const { components, selectedComponentId } = useDashboardStore();
  const selectedComp = components.find((c) => c.id === selectedComponentId);

  const tabs = useMemo(() => {
    if (!selectedComp) return [];
    // 2026-10-05（用户反馈）：数据绑定统一收敛到「交互联动」内一份，通用属性/控件特有不再重复展示
    const eventsTab = (hint?: ReactNode) => (
      <>
        {hint}
        <EventForm comp={selectedComp} />
        <div style={{ borderTop: '1px solid #333', marginTop: 8 }}>
          <DataBindingPanel />
        </div>
      </>
    );
    if (selectedComp.type === 'device-table') {
      // 设备列表直读设备中枢+注册表（阶段3/4），不走通用数据源
      return [
        { key: 'base', label: '通用属性', children: <BaseForm comp={selectedComp} /> },
        { key: 'special', label: '控件特有', children: <SpecialForm comp={selectedComp} /> },
        {
          key: 'events',
          label: '交互联动',
          children: eventsTab(
            <div style={{ padding: '0 0 8px', color: '#888', fontSize: 12 }}>
              「设备列表」自动读取设备实时中枢 + 设备注册表（刷新后设备不丢），无需配置数据源；
              可在「控件特有」点位列（如 io.ai0,network.cellular.rssiDbm）展示具体点位。
            </div>,
          ),
        },
      ];
    }
    return [
      { key: 'base', label: '通用属性', children: <BaseForm comp={selectedComp} /> },
      { key: 'special', label: '控件特有', children: <SpecialForm comp={selectedComp} /> },
      { key: 'events', label: '交互联动', children: eventsTab() },
    ];
  }, [selectedComponentId, components]);

  if (!selectedComp) {
    return (
      <div style={{ padding: 24, color: '#888' }}>
        <Empty description="选中组件后可配置属性" />
      </div>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title level={5} style={{ margin: 0, fontSize: 13, color: '#e0e0e0' }}>属性配置</Title>
        <Text style={{ fontSize: 11, color: '#1890ff', background: 'rgba(24,144,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>
          {widgetRegistry.getLabel(selectedComp.type)}
        </Text>
      </div>

      <Divider style={{ margin: '12px 0', borderColor: '#333' }} />
      <Tabs size="small" items={tabs} />
    </div>
  );
}
